import React, { useEffect, useRef } from 'react';

interface ListingMapProps {
  latitude: number;
  longitude: number;
  showExact: boolean;
  title?: string;
}

// Google Maps type definitions
interface GoogleMapsLatLng {
  lat: number;
  lng: number;
}

interface GoogleMapsMap {
  setCenter: (center: GoogleMapsLatLng) => void;
  setZoom: (zoom: number) => void;
}

interface GoogleMapsMarker {
  setMap: (map: GoogleMapsMap | null) => void;
}

interface GoogleMapsCircle {
  setMap: (map: GoogleMapsMap | null) => void;
}

interface GoogleMapsAPI {
  maps: {
    Map: new (element: HTMLElement, options: Record<string, unknown>) => GoogleMapsMap;
    Marker: new (options: Record<string, unknown>) => GoogleMapsMarker;
    Circle: new (options: Record<string, unknown>) => GoogleMapsCircle;
  };
}

declare global {
  interface Window {
    google?: GoogleMapsAPI;
    initMap?: () => void;
  }
}

const ListingMap: React.FC<ListingMapProps> = ({ latitude, longitude, showExact, title }) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<GoogleMapsMap | null>(null);
  const markerRef = useRef<GoogleMapsMarker | null>(null);
  const circleRef = useRef<GoogleMapsCircle | null>(null);

  useEffect(() => {
    const loadGoogleMaps = () => {
      // Check if Google Maps is already loaded
      if (window.google && window.google.maps) {
        initializeMap();
        return;
      }

      // Check if script is already being loaded
      if (document.querySelector('script[src*="maps.googleapis.com"]')) {
        // Wait for it to load
        window.initMap = initializeMap;
        return;
      }

      // Load Google Maps script
      const script = document.createElement('script');
      const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&callback=initMap`;
      script.async = true;
      script.defer = true;
      window.initMap = initializeMap;
      document.head.appendChild(script);
    };

    const initializeMap = () => {
      if (!mapRef.current || !window.google) return;

      const position = { lat: latitude, lng: longitude };

      // Create map
      mapInstanceRef.current = new window.google.maps.Map(mapRef.current, {
        center: position,
        zoom: showExact ? 15 : 13,
        mapTypeControl: false,
        fullscreenControl: true,
        streetViewControl: false,
        zoomControl: true,
      });

      // Clear existing marker and circle
      if (markerRef.current) {
        markerRef.current.setMap(null);
      }
      if (circleRef.current) {
        circleRef.current.setMap(null);
      }

      if (showExact) {
        // Show exact pin location
        markerRef.current = new window.google.maps.Marker({
          position,
          map: mapInstanceRef.current,
          title: title || 'Listing Location',
        });
      } else {
        // Show approximate location with circle (0.25 mile radius)
        // 0.25 miles ≈ 402 meters
        circleRef.current = new window.google.maps.Circle({
          strokeColor: '#3B82F6',
          strokeOpacity: 0.8,
          strokeWeight: 2,
          fillColor: '#3B82F6',
          fillOpacity: 0.2,
          map: mapInstanceRef.current,
          center: position,
          radius: 402, // meters
        });
      }
    };

    loadGoogleMaps();

    // Cleanup
    return () => {
      if (markerRef.current) {
        markerRef.current.setMap(null);
      }
      if (circleRef.current) {
        circleRef.current.setMap(null);
      }
    };
  }, [latitude, longitude, showExact, title]);

  if (!process.env.REACT_APP_GOOGLE_MAPS_API_KEY) {
    return (
      <div className="bg-gray-100 p-4 rounded-lg text-center">
        <p className="text-gray-600">Map not available (API key not configured)</p>
      </div>
    );
  }

  return (
    <div className="w-full">
      <div ref={mapRef} className="w-full h-96 rounded-lg" />
      {!showExact && (
        <p className="text-sm text-gray-500 mt-2">
          Approximate location shown within 0.25 mile radius for privacy
        </p>
      )}
    </div>
  );
};

export default ListingMap;

